// @flow
/* eslint-disable no-plusplus, no-underscore-dangle */
import React, { Component } from 'react';
import Connect from '@rover/react-lib/components/utils/Redux/Connect';
import { lifted as liftedCore } from '@rover/react-lib/lib/lifted-components/core';

export const MOUNT_LIFTED_COMPONENT: '@lifted-components/MOUNT_LIFTED_COMPONENT' =
  '@lifted-components/MOUNT_LIFTED_COMPONENT';
export const UNMOUNT_LIFTED_COMPONENT: '@lifted-components/UNMOUNT_LIFTED_COMPONENT' =
  '@lifted-components/UNMOUNT_LIFTED_COMPONENT';
export const UPDATE_LIFTED_COMPONENT: '@lifted-components/UPDATE_LIFTED_COMPONENT' =
  '@lifted-components/UPDATE_LIFTED_COMPONENT';

type MountComponentAction = {
  type: typeof MOUNT_LIFTED_COMPONENT,
  id: string,
  name: ?string,
};
const mountComponent = (id, name): MountComponentAction => ({
  type: MOUNT_LIFTED_COMPONENT,
  id,
  name,
});

type UnmountComponentAction = {
  type: typeof UNMOUNT_LIFTED_COMPONENT,
  id: string,
};
const unmountComponent = (id): UnmountComponentAction => ({
  type: UNMOUNT_LIFTED_COMPONENT,
  id,
});

export type UpdateComponentAction<State> = {
  type: typeof UPDATE_LIFTED_COMPONENT,
  id: string,
  updates: $Shape<State>,
};
const updateComponent = <State>(id, updates): UpdateComponentAction<State> => ({
  type: UPDATE_LIFTED_COMPONENT,
  id,
  updates,
});

type ActionTypes = MountComponentAction | UnmountComponentAction | UpdateComponentAction<any>;

export const reducer = (state: {} = {}, action: ActionTypes) => {
  switch (action.type) {
    case MOUNT_LIFTED_COMPONENT:
      if (state[action.id]) {
        throw new Error(
          `[LiftedComponents] Error mounting lifted component ${
            action.name ? action.name : ''
          }: An instance of this component is already mounted.`
        );
      }
      return state;
    case UNMOUNT_LIFTED_COMPONENT: {
      const { [action.id]: removedState, ...rest } = state;
      return rest;
    }
    case UPDATE_LIFTED_COMPONENT:
      return {
        ...state,
        [action.id]: {
          ...(state[action.id] || {}),
          ...action.updates,
        },
      };
    default:
      return state;
  }
};

let id = 0;

export type ReduxLiftedComponentClass<Props, State> = Class<
  Component<Props & { innerRef?: any => any }>
> & {
  select: any => $Shape<State>,
  update: (updates: $Shape<State>) => UpdateComponentAction<State>,
  matchAction: (action: { type: string, id: string }) => boolean,
};

export const lifted = <Props: {}, State: {}>(
  WrappedComponent: Class<Component<Props, State>>
): ReduxLiftedComponentClass<Props, State> => {
  const LiftedComponent = liftedCore(WrappedComponent);
  const liftedId = `lifted_${id++}`;
  const select = state => state.lifted[liftedId] || LiftedComponent.initialState;
  const update = updates => updateComponent(liftedId, updates);
  const matchAction = action => action.type === UPDATE_LIFTED_COMPONENT && action.id === liftedId;
  const selector = state => ({
    _componentState: select(state),
  });
  const actions = {
    // $FlowFixMe This needs to be refactored to use ducks
    _onUpdate: update,
  };

  return class ReduxLifted extends Component<Props & { innerRef?: any => any }> {
    static displayName = `ReduxLifted${WrappedComponent.displayName ||
      WrappedComponent.name ||
      ''}`;

    static select = state => select(state) || {};
    static update = update;
    static matchAction = matchAction;

    render() {
      const { innerRef, ...other } = this.props;
      return (
        // $FlowFixMe This needs to be refactored to use ducks
        <Connect
          selector={selector}
          // $FlowFixMe This needs to be refactored to use ducks
          actions={actions}
          onMount={() => mountComponent(liftedId, ReduxLifted.displayName)}
          onUnmount={() => unmountComponent(liftedId)}
        >
          {({ _componentState, _onUpdate }) => (
            <LiftedComponent
              {...other}
              ref={innerRef}
              state={_componentState}
              onLiftedChange={_onUpdate}
            />
          )}
        </Connect>
      );
    }
  };
};



// WEBPACK FOOTER //
// ../react-lib/src/lib/lifted-components/redux/index.jsx